// Thin LOCAL fetch client for the THU-346 (Slice 3) Prescribe endpoints — the cockpit's prescriptions,
// persisted to the FB-authorized synthetic demo clinical store. MOCK prescribe: rows persist to the demo
// store only and are NEVER transmitted to a pharmacy. Deliberately NOT in the published SDK yet
// (local-first). Mirrors the SDK HttpClient shape: baseUrl + getToken → Bearer, credentials:"include"
// (dev cookie), unwraps the { data } envelope.
import { useMemo } from "react";
import { API_ORIGIN } from "../config";
import { useAppAuth } from "../auth";

export interface Prescription {
  id: string;
  drug: string;
  dosage: string;
  sig: string;
  notes: string | null;
  status: "active" | "discontinued";
  created_at?: string;
}
export interface PrescriptionInput {
  drug: string;
  dosage?: string;
  sig?: string;
  notes?: string;
}

export interface PrescriptionsApi {
  list(patientId: string): Promise<Prescription[]>;
  create(patientId: string, rx: PrescriptionInput): Promise<Prescription>;
  remove(patientId: string, rxId: string): Promise<void>;
}

export function usePrescriptionsApi(): PrescriptionsApi {
  const { getToken } = useAppAuth();
  return useMemo<PrescriptionsApi>(() => {
    async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
      const headers: Record<string, string> = {};
      if (body !== undefined) headers["Content-Type"] = "application/json";
      const token = await getToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`${API_ORIGIN}${path}`, {
        method,
        headers,
        credentials: "include",
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      const text = await res.text();
      const json: unknown = text ? JSON.parse(text) : {};
      if (!res.ok) {
        const err = (json as { error?: { code?: string; message?: string } }).error;
        throw new Error(err?.message ?? "Request failed");
      }
      return (json as { data?: T }).data as T;
    }
    const base = (patientId: string) =>
      `/v1/doctor/patients/${encodeURIComponent(patientId)}/prescriptions`;
    return {
      list: (patientId) => request<Prescription[]>("GET", base(patientId)),
      create: (patientId, rx) => request<Prescription>("POST", base(patientId), rx),
      remove: (patientId, rxId) =>
        request<void>("DELETE", `${base(patientId)}/${encodeURIComponent(rxId)}`),
    };
  }, [getToken]);
}
